
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";

interface ScheduleLogsFiltersProps {
  filterType: string;
  setFilterType: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
  selectedGuard: string;
  setSelectedGuard: (value: string) => void;
  selectedAdmin: string;
  setSelectedAdmin: (value: string) => void;
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
}

const ScheduleLogsFilters: React.FC<ScheduleLogsFiltersProps> = ({
  filterType,
  setFilterType,
  searchTerm,
  setSearchTerm,
  selectedTeam,
  setSelectedTeam,
  selectedGuard,
  setSelectedGuard,
  selectedAdmin,
  setSelectedAdmin,
  selectedDate,
  setSelectedDate,
}) => {
  const { user } = useSupabaseUser();
  const { isSuperAdmin, adminTeamIds } = useUserRoles();

  // Fetch teams - only teams the admin has access to
  const { data: teams } = useQuery({
    queryKey: ['teams-for-logs', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
      if (error) throw error;
      
      // Filter teams based on admin access (RLS will handle this)
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch guards - only guards in the admin's accessible teams/groups
  const { data: guards } = useQuery({
    queryKey: ['guards-for-logs', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Use the profiles view that respects admin group boundaries
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name')
        .order('first_name');
      
      if (error) throw error;
      
      // RLS policies will ensure only accessible profiles are returned
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch admins - only admins in the same admin group
  const { data: admins } = useQuery({
    queryKey: ['admins-for-logs', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data: shiftChanges, error } = await supabase
        .from('shift_changes')
        .select('created_by')
        .not('created_by', 'is', null);
      
      if (error) throw error;
      
      const uniqueAdminIds = [...new Set(shiftChanges?.map(item => item.created_by).filter(Boolean))];
      
      if (uniqueAdminIds.length === 0) return [];
      
      // Fetch admin profiles - RLS will filter to accessible admins only
      const { data: adminProfiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name')
        .in('id', uniqueAdminIds)
        .order('first_name');
        
      if (profileError) throw profileError;
      return adminProfiles || [];
    },
    enabled: !!user,
  });

  const clearFilters = () => {
    setFilterType("all");
    setSearchTerm("");
    setSelectedTeam("all");
    setSelectedGuard("all");
    setSelectedAdmin("all");
    setSelectedDate(undefined);
  };

  return (
    <div className="space-y-4">
      {/* First row of filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Changes</SelectItem>
            <SelectItem value="added">Added</SelectItem>
            <SelectItem value="removed">Removed</SelectItem>
            <SelectItem value="modified">Modified</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedTeam} onValueChange={setSelectedTeam}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by team" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            {teams?.map((team) => (
              <SelectItem key={team.id} value={team.name}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedGuard} onValueChange={setSelectedGuard}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by personnel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Personnel</SelectItem>
            {guards?.map((guard) => {
              const guardName = guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim() || 'Unknown Guard';
              return (
                <SelectItem key={guard.id} value={guardName}>
                  {guardName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Second row of filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select value={selectedAdmin} onValueChange={setSelectedAdmin}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by admin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Admins</SelectItem>
            {admins?.map((admin) => {
              const adminName = admin.full_name || `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || 'Unknown Admin';
              return (
                <SelectItem key={admin.id} value={adminName}>
                  {adminName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full sm:w-48 justify-start text-left font-normal",
                !selectedDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[560px] max-w-[90vw] p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        <Input
          placeholder="Search by guard, team, or creator..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1"
        />

        <Button variant="outline" onClick={clearFilters}>
          Clear Filters
        </Button>
      </div>
    </div>
  );
};

export default ScheduleLogsFilters;
